import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button
} from '@mui/material'
import {
  TrendingUp as ProgressIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  Chat as ChatIcon,
  MenuBook as ReadingIcon,
  Headphones as ListeningIcon,
  Edit as WritingIcon,
  Translate as VocabIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
// import { apiClient, API_ENDPOINTS } from '../config/api' // For future API integration
import toast from 'react-hot-toast'

// Mock data for demonstration
const mockProgressData = [
  { date: '2024-01-01', grammar: 65, conversation: 70, writing: 60, vocabulary: 75, reading: 68, listening: 72 },
  { date: '2024-01-08', grammar: 68, conversation: 73, writing: 65, vocabulary: 78, reading: 71, listening: 75 },
  { date: '2024-01-15', grammar: 72, conversation: 76, writing: 68, vocabulary: 80, reading: 74, listening: 78 },
  { date: '2024-01-22', grammar: 75, conversation: 79, writing: 72, vocabulary: 83, reading: 77, listening: 81 },
  { date: '2024-01-29', grammar: 78, conversation: 82, writing: 75, vocabulary: 85, reading: 80, listening: 84 }
]

const mockActivities = [
  { type: 'Grammar', icon: <AssignmentIcon />, count: 25, lastUsed: '2024-01-29', avgScore: 78 },
  { type: 'Conversation', icon: <ChatIcon />, count: 18, lastUsed: '2024-01-29', avgScore: 82 },
  { type: 'Writing', icon: <WritingIcon />, count: 12, lastUsed: '2024-01-28', avgScore: 75 },
  { type: 'Vocabulary', icon: <VocabIcon />, count: 30, lastUsed: '2024-01-29', avgScore: 85 },
  { type: 'Reading', icon: <ReadingIcon />, count: 15, lastUsed: '2024-01-27', avgScore: 80 },
  { type: 'Listening', icon: <ListeningIcon />, count: 20, lastUsed: '2024-01-28', avgScore: 84 }
]

const mockAchievements = [
  { title: 'First Steps', description: 'Hoàn thành bài học đầu tiên', earned: true, date: '2024-01-01' },
  { title: 'Grammar Master', description: 'Đạt 80% trong 10 bài grammar liên tiếp', earned: true, date: '2024-01-15' },
  { title: 'Conversation Pro', description: 'Thực hiện 20 cuộc hội thoại', earned: true, date: '2024-01-20' },
  { title: 'Writing Expert', description: 'Viết 15 bài luận', earned: false, progress: 12 },
  { title: 'Vocabulary Champion', description: 'Học 500 từ mới', earned: false, progress: 387 },
  { title: 'Reading Enthusiast', description: 'Đọc 25 bài văn', earned: false, progress: 15 }
]

function Progress() {
  const [progressData, setProgressData] = useState(mockProgressData)
  const [activities, setActivities] = useState(mockActivities)
  const [achievements, setAchievements] = useState(mockAchievements)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState('month')

  const loadProgressData = async () => {
    setIsLoading(true)
    try {
      // In a real app, this would fetch from the API
// const response = await apiClient.get(API_ENDPOINTS.ASSESSMENT.PROGRESS)
      // setProgressData(response.data.progress)
      // setActivities(response.data.activities)
      // setAchievements(response.data.achievements)
      
      // For now, using mock data
      toast.success('Dữ liệu tiến độ đã được cập nhật!')
    } catch (error) {
      console.error('Progress loading error:', error)
      toast.error('Có lỗi xảy ra khi tải dữ liệu tiến độ')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadProgressData()
  }, [])

  const calculateOverallProgress = () => {
    const latestData = progressData[progressData.length - 1]
    if (!latestData) return 0
    
    const skills = ['grammar', 'conversation', 'writing', 'vocabulary', 'reading', 'listening']
    const total = skills.reduce((sum, skill) => sum + latestData[skill], 0)
    return Math.round(total / skills.length)
  }

  const getSkillColor = (skill) => {
    const colors = {
      grammar: '#FF6B6B',
      conversation: '#4ECDC4',
      writing: '#45B7D1',
      vocabulary: '#96CEB4',
      reading: '#FFEAA7',
      listening: '#DDA0DD'
    }
    return colors[skill] || '#95A5A6'
  }

  const overallProgress = calculateOverallProgress()
  const earnedAchievements = achievements.filter(a => a.earned).length

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ProgressIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Tiến độ học tập
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Theo dõi quá trình học tiếng Anh của bạn
          </Typography>
        </Box>

        {/* Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {overallProgress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tiến độ tổng thể
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <ScheduleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {activities.reduce((sum, a) => sum + a.count, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bài học đã hoàn thành
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <StarIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  {earnedAchievements}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Thành tựu đạt được
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                  29
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ngày học liên tiếp
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          {/* Progress Chart */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Biểu đồ tiến độ theo thời gian</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadProgressData}
                  disabled={isLoading}
                  startIcon={<RefreshIcon />}
                >
                  Cập nhật
                </Button>
              </Box>
              {isLoading && <LinearProgress sx={{ mb: 2 }} />}
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={progressData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="grammar" stroke="#FF6B6B" strokeWidth={2} />
                  <Line type="monotone" dataKey="conversation" stroke="#4ECDC4" strokeWidth={2} />
                  <Line type="monotone" dataKey="writing" stroke="#45B7D1" strokeWidth={2} />
                  <Line type="monotone" dataKey="vocabulary" stroke="#96CEB4" strokeWidth={2} />
                  <Line type="monotone" dataKey="reading" stroke="#FFEAA7" strokeWidth={2} />
                  <Line type="monotone" dataKey="listening" stroke="#DDA0DD" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Activity Summary */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={3} sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Hoạt động gần đây</Typography>
              <List>
                {activities.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        {activity.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.type}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {activity.count} bài • Điểm TB: {activity.avgScore}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Lần cuối: {activity.lastUsed}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={`${activity.avgScore}%`} 
                        size="small" 
                        color={activity.avgScore >= 80 ? 'success' : activity.avgScore >= 60 ? 'warning' : 'error'}
                      />
                    </ListItem>
                    {index < activities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Skills Progress */}
          <Grid item xs={12} lg={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>Tiến độ theo kỹ năng</Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[progressData[progressData.length - 1]]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="grammar" fill="#FF6B6B" name="Grammar" />
                  <Bar dataKey="conversation" fill="#4ECDC4" name="Conversation" />
                  <Bar dataKey="writing" fill="#45B7D1" name="Writing" />
                  <Bar dataKey="vocabulary" fill="#96CEB4" name="Vocabulary" />
                  <Bar dataKey="reading" fill="#FFEAA7" name="Reading" />
                  <Bar dataKey="listening" fill="#DDA0DD" name="Listening" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Achievements */}
          <Grid item xs={12} lg={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Thành tựu</Typography>
              <List>
                {achievements.map((achievement, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>
                        {achievement.earned ? (
                          <StarIcon sx={{ color: 'warning.main' }} />
                        ) : (
                          <StarIcon sx={{ color: 'grey.400' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ 
                              textDecoration: achievement.earned ? 'none' : 'none',
                              color: achievement.earned ? 'text.primary' : 'text.secondary'
                            }}>
                              {achievement.title}
                            </Typography>
                            {achievement.earned && (
                              <Chip label="Đạt được" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {achievement.description}
                            </Typography>
                            {!achievement.earned && achievement.progress && (
                              <Box sx={{ mt: 1 }}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={(achievement.progress / (achievement.title.includes('Writing') ? 15 : achievement.title.includes('Vocabulary') ? 500 : 25)) * 100}
                                  sx={{ height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {achievement.progress}/{achievement.title.includes('Writing') ? 15 : achievement.title.includes('Vocabulary') ? 500 : 25}
                                </Typography>
                              </Box>
                            )}
                            {achievement.earned && (
                              <Typography variant="caption" color="success.main">
                                Đạt được vào {achievement.date}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < achievements.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Tips */}
        <Paper elevation={2} sx={{ p: 3, mt: 4, bgcolor: 'success.50' }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
            📈 Mẹo cải thiện tiến độ học tập
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Học đều đặn mỗi ngày, dù chỉ 15-30 phút</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Tập trung vào kỹ năng yếu nhất của bạn</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1 }}>• Đặt mục tiêu cụ thể cho từng tuần</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>• Ôn tập kiến thức cũ thường xuyên</Typography>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
    </Container>
  )
}

export default Progress